import type { App } from "vue";
import { cache, CacheError } from "@/services/cache";
import { onCacheIncident, type CacheIncident } from "@/services/cache/cache-database";
import { purgeLegacyDatabases } from "@/services/cache/cache-maintenance";
import { clientStateManager } from "@/services/error-services";
import { createDefaultLogger, LogLevels } from "@/types/console-logger";

const logger = createDefaultLogger( LogLevels.DEBUG );

/** The logger is a no-op in production: route the cache's user-visible failure modes into the client-error channel (F-I2). */
export function reportCacheIncident( incident: CacheIncident ): void {
    clientStateManager.queueClientError(
        new CacheError( `[ cache ] incident: ${ incident.kind } on "${ incident.database }"`, { cause: incident.error } ),
        { cacheIncident: incident.kind, database: incident.database }
    );
}

/**
 * Starts the database open as early as possible so the first store read finds it settled. Every store
 * method awaits the open itself, so nothing depends on this having finished. The legacy purge runs after
 * the open, off the render path. Installed on the main thread only, so the sync worker never reports
 * incidents through the window's client-error channel.
 */
const CachePlugin = {
    install( _: App< Element > ): void {
        onCacheIncident( reportCacheIncident );
        void cache.ready()
            .then( () => purgeLegacyDatabases() )
            .catch( ( err: unknown ) => logger.error( `[ cache ] initialization failed: ${ ( err as Error )?.message ?? err }` ) );
    }
};

export default CachePlugin;
